import { Select, Button } from './ui';
import { Session } from '../types/session';
import { CONFERENCE_TIMEZONE } from '../lib/timezone';

export interface FilterState {
  timeRange: string;
  location: string;
}

interface FilterPanelProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  onClear: () => void;
  sessions: Session[];
  searchTerm?: string;
}

export default function FilterPanel({ filters, onChange, onClear, sessions, searchTerm = '' }: FilterPanelProps) {
  // Determine if sessions span multiple dates
  const uniqueDates = Array.from(
    new Set(
      sessions.map((session) =>
        new Date(session.startTime).toLocaleDateString('en-US', {
          timeZone: CONFERENCE_TIMEZONE,
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      )
    )
  );
  const isMultiDay = uniqueDates.length > 1;

  // Multi-day: show dates. Single-day: show time slots.
  const timeRanges = isMultiDay
    ? uniqueDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
    : Array.from(
        new Set(
          sessions.map((session) =>
            new Date(session.startTime).toLocaleTimeString('en-US', {
              timeZone: CONFERENCE_TIMEZONE,
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })
          )
        )
      ).sort((a, b) => {
        const parseTime = (t: string) => {
          const [time, period] = t.split(' ');
          let [h, m] = time.split(':').map(Number);
          if (period === 'PM' && h !== 12) h += 12;
          if (period === 'AM' && h === 12) h = 0;
          return h * 60 + m;
        };
        return parseTime(a) - parseTime(b);
      });

  const timeFilterLabel = isMultiDay ? 'Date' : 'Time Slot';
  const timeFilterDefault = isMultiDay ? 'All Dates' : 'All Time Slots';

  // Extract unique locations (buildings and rooms)
  const locations = Array.from(
    new Set(
      sessions.map((session) => `${session.room.building} - ${session.room.name}`)
    )
  ).sort();

  const buildings = Array.from(
    new Set(sessions.map((session) => session.room.building))
  ).sort();

  const hasActiveFilters = filters.timeRange !== '' || filters.location !== '' || searchTerm !== '';

  return (
    <div className="bg-white rounded-lg shadow-md p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-usc-gray-900">Filters</h3>
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClear}
          >
            Clear All
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label={timeFilterLabel}
          value={filters.timeRange}
          onChange={(e) => onChange({ ...filters, timeRange: e.target.value })}
        >
          <option value="">{timeFilterDefault}</option>
          {timeRanges.map((range) => (
            <option key={range} value={range}>
              {range}
            </option>
          ))}
        </Select>

        <Select
          label="Location"
          value={filters.location}
          onChange={(e) => onChange({ ...filters, location: e.target.value })}
        >
          <option value="">All Locations</option>
          <optgroup label="Buildings">
            {buildings.map((building) => (
              <option key={building} value={building}>
                {building}
              </option>
            ))}
          </optgroup>
          <optgroup label="Specific Rooms">
            {locations.map((location) => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </optgroup>
        </Select>
      </div>
    </div>
  );
}
